import { create } from "zustand";

export const GOOGLE_CLIENT_ID = "180721207737-66hk4uutpagv7eesaktbqtjteippq2dc.apps.googleusercontent.com";

export interface User { email: string; name: string }
export interface ScoreMeta { id: string; title: string; updated_at: number; video_id?: string | null; bpm?: number | null; key?: string | null }

interface AuthState {
  user: User | null;
  ready: boolean;            // /api/auth/me has resolved at least once
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  ready: false,
  refresh: async () => {
    try {
      const r = await fetch("/api/auth/me", { credentials: "same-origin" });
      const d = await r.json();
      set({ user: d.user ?? null, ready: true });
    } catch {
      set({ ready: true });
    }
  },
  logout: async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }); } catch { /* ignore */ }
    set({ user: null });
  },
}));

// ---- Google Identity Services ----
declare global { interface Window { google?: any } }
let gisPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("GIS load failed"));
    document.head.appendChild(s);
  });
  return gisPromise;
}

let inited = false;
async function initGoogle() {
  await loadGis();
  if (inited) return;
  inited = true;
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: async (resp: { credential: string }) => {
      try {
        await fetch("/api/auth/google", {
          method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential: resp.credential }),
        });
        await useAuth.getState().refresh();
      } catch { /* ignore */ }
    },
  });
}

// render the official Google button into el (re-renders if called again)
export async function renderGoogleButton(el: HTMLElement) {
  await initGoogle();
  el.innerHTML = "";
  window.google.accounts.id.renderButton(el, { theme: "filled_blue", size: "large", shape: "pill", text: "signin_with", width: 240 });
}

// ---- saved scores API ----
export async function listScores(): Promise<{ scores: ScoreMeta[]; max: number }> {
  const r = await fetch("/api/scores", { credentials: "same-origin" });
  if (!r.ok) throw new Error("list failed");
  return r.json();
}
export async function saveScore(body: { id?: string; title: string; data: unknown }): Promise<{ id?: string; error?: string; limit?: number; status: number }> {
  const r = await fetch("/api/scores", {
    method: "POST", credentials: "same-origin",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  return { ...d, status: r.status };
}
export async function getScore(id: string): Promise<{ title: string; data: any }> {
  const r = await fetch(`/api/scores/${id}`, { credentials: "same-origin" });
  if (!r.ok) throw new Error("get failed");
  return r.json();
}
export async function deleteScore(id: string): Promise<void> {
  await fetch(`/api/scores/${id}`, { method: "DELETE", credentials: "same-origin" });
}
